import React, { useState } from "react";

import {
  InputChangeEvent,
  IScheduledTaskPropertyRef,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteEvent, DataTableRowEditEvent } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";

interface ITaskConfigIndexedDatasetTableProps extends IScheduledTaskPropertyRef {
  header: string;
  datasets: string[];
  onChange: (e: InputChangeEvent<string[]>) => void;
  bodyClass?: string;
}

interface IDatasetRowModel {
  value: { dataset: string | null } | null;
  newValue: { dataset: string | null } | null;
  id: string;
}

export default function TaskConfigIndexedDatasetTable(props: ITaskConfigIndexedDatasetTableProps) {
  const [dataArr, setDataArr] = useState<IDatasetRowModel[]>(mapToList(props.datasets));
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

  const propagateChangesStateUp = (arr: IDatasetRowModel[]) => {
    const tmp = { property: props.property, value: arr.map(e => e.value?.dataset!!) } as InputChangeEvent<
      string[]
    >;
    props.onChange(tmp);
  };

  const confirmWrapper = (event: any, confirmHandler: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-dataset-mapping"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = (warn: string) => {
    easasAlert({ summary: T("alert.invalid"), severity: "warn", detail: warn, life: 3000 });
  };

  const handleAdd = () => {
    const newRow = { value: { dataset: null }, newValue: { dataset: null }, id: v4() } as IDatasetRowModel;
    setDataArr(prev => [...prev, newRow]);
    setEditingRows(prev => ({ ...prev, [newRow.id]: true }));
  };

  const handleDelete = (index: number) => {
    setDataArr(prev => {
      const filtered = prev.toSpliced(index, 1);
      propagateChangesStateUp(filtered);
      return filtered;
    });
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{props.header}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()} />
    </div>
  );

  const indexBodyTemplate = (row: IDatasetRowModel) => {
    return <span>{dataArr.indexOf(row)}</span>;
  };

  const datasetBodyTemplate = (row: IDatasetRowModel) => <DatasetTitle identifier={row.value?.dataset!!} />;

  const datasetEditorTemplate = (opt: ColumnEditorOptions) => (
    <DatasetDropdown value={opt.rowData.newValue.dataset} onChange={e => opt.editorCallback?.(e)} />
  );

  const renderDeleteButton = (_: IDatasetRowModel, options: ColumnBodyOptions) => {
    return (
      <Button
        icon="pi pi-trash"
        className="const-del-btn  p-button-rounded p-button-text p-button-danger"
        onClick={e => confirmWrapper(e, () => handleDelete(options.rowIndex))}
      />
    );
  };

  const onRowReorder = (e: any) => {
    const updated = [...e.value];
    setDataArr(updated);
    propagateChangesStateUp(updated);
  };

  const handleRowEditInit = (e: DataTableRowEditEvent) => {
    const data = e.data as IDatasetRowModel;
    data.newValue = { ...data.value! };
  };

  const checkForDuplicates = (newData: IDatasetRowModel) => {
    const duplicates = dataArr.filter(
      it => it.value?.dataset === newData?.newValue?.dataset && it.id !== newData?.id
    ).length;
    if (duplicates > 0) return true;
    return false;
  };

  const handleRowEditCancel = (e: DataTableRowEditEvent) => {
    const data = e.data as IDatasetRowModel;
    if (data.newValue?.dataset === null || checkForDuplicates(data)) {
      setDataArr(prev => prev.filter(item => item.id !== data.id));
      setEditingRows(prev => ({ ...prev, [data.id]: false }));
    }
    data.newValue = null;
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as IDatasetRowModel;

    if (newData?.newValue?.dataset === null || checkForDuplicates(newData)) return;

    setDataArr(prev => {
      const updated = prev.map((item, index) =>
        index === e.index ? { ...item, value: newData.newValue } : item
      );
      propagateChangesStateUp(updated);
      return updated;
    });
  };

  const onRowEditChange = (e: DataTableRowEditEvent) => {
    const newData = dataArr.at(e.index);
    const exist = e.data[newData!.id];

    if (newData?.newValue?.dataset === null) {
      if (!exist) {
        showWarn(T("alert.datasets-must-be-selected"));
        return;
      }
    }
    if (!exist) {
      if (checkForDuplicates(newData!)) {
        showWarn(T("alert.dataset-already-exists-in-list"));
        return;
      }
    }
    setEditingRows(e.data);
  };

  return (
    <DataTable
      value={dataArr}
      header={headerTemplate}
      dataKey="id"
      editMode="row"
      onRowReorder={onRowReorder}
      onRowEditInit={handleRowEditInit}
      onRowEditCancel={handleRowEditCancel}
      onRowEditComplete={handleRowEditComplete}
      onRowEditChange={onRowEditChange}
      editingRows={editingRows}
      width="100%"
      className={props.bodyClass}
      size="small"
      reorderableRows
    >
      <Column rowReorder style={{ width: "3em" }} />
      <Column header={T("word.index")} body={indexBodyTemplate} style={{ width: "4rem" }} />
      <Column
        field="newValue.dataset"
        header={T("word.dataset")}
        body={datasetBodyTemplate}
        editor={datasetEditorTemplate}
      />
      <Column rowEditor align="right" className="w-5rem p-0" />
      <Column body={renderDeleteButton} className="w-3rem p-0" />
    </DataTable>
  );
}

function mapToList(map: string[]): IDatasetRowModel[] {
  return map.map(item => {
    return { value: { dataset: item }, id: v4() } as IDatasetRowModel;
  });
}
